// Ordered-dither dot field: an image + drifting cloud noise rendered as a fine grid of tiny squares.
// Density (not opacity) carries the tone, like a halftone print. The cursor stirs a low-res
// velocity field that smears the image sampling, so dots flow around it like ink in water.

const BAYER_8 = [
  0, 32, 8, 40, 2, 34, 10, 42, 48, 16, 56, 24, 50, 18, 58, 26, 12, 44, 4, 36, 14, 46, 6, 38, 60, 28, 52, 20, 62, 30,
  54, 22, 3, 35, 11, 43, 1, 33, 9, 41, 51, 19, 59, 27, 49, 17, 57, 25, 15, 47, 7, 39, 13, 45, 5, 37, 63, 31, 55, 23,
  61, 29, 53, 21,
].map((v) => (v + 0.5) / 64);

const NOISE = 256;
const FLICKER = 0.09; // amplitude of the living shimmer
const RAND_MASK = 65535;
const RAND = new Float32Array(RAND_MASK + 1).map(() => Math.random());
const FIELD_STEP = 6; // cells per velocity-field cell

export type DitherScene = {
  cols: number;
  rows: number;
  cell: number;
  image: Float32Array | null;
  noise: Float32Array;
  small: HTMLCanvasElement;
  smallCtx: CanvasRenderingContext2D;
  pixels: ImageData;
  mask: CanvasPattern | null;
  // velocity field (in cells) + "stir" energy
  fw: number;
  fh: number;
  vx: Float32Array;
  vy: Float32Array;
  energy: Float32Array;
  tmp: Float32Array;
  // trail buffers (allocated on demand)
  tLift?: Float32Array;
  tDx?: Float32Array;
  tDy?: Float32Array;
};

let noiseCache: Float32Array | null = null;

function buildNoise(): Float32Array {
  // Built once and reused — resizing the window no longer regenerates it
  if (noiseCache) return noiseCache;
  // Tileable two-octave value noise → soft clouds
  const out = new Float32Array(NOISE * NOISE);
  noiseCache = out;
  const s = (t: number) => t * t * (3 - 2 * t);
  const octave = (g: number, amp: number) => {
    const lattice = new Float32Array(g * g).map(() => Math.random());
    const at = (ix: number, iy: number) => lattice[(iy % g) * g + (ix % g)];
    for (let y = 0; y < NOISE; y++) {
      for (let x = 0; x < NOISE; x++) {
        const fx = (x / NOISE) * g;
        const fy = (y / NOISE) * g;
        const x0 = Math.floor(fx);
        const y0 = Math.floor(fy);
        const tx = s(fx - x0);
        const ty = s(fy - y0);
        const a = at(x0, y0) + (at(x0 + 1, y0) - at(x0, y0)) * tx;
        const b = at(x0, y0 + 1) + (at(x0 + 1, y0 + 1) - at(x0, y0 + 1)) * tx;
        out[y * NOISE + x] += (a + (b - a) * ty) * amp;
      }
    }
  };
  octave(6, 0.62);
  octave(14, 0.28);
  octave(32, 0.1);
  return out;
}

export function createDitherScene(width: number, height: number, cell: number, dotSize: number, dpr: number): DitherScene {
  const cols = Math.ceil(width / cell);
  const rows = Math.ceil(height / cell);
  const small = document.createElement('canvas');
  small.width = cols;
  small.height = rows;
  const smallCtx = small.getContext('2d')!;

  // One square per cell (device pixels) — used with destination-in to cut the gaps between dots
  const tile = document.createElement('canvas');
  tile.width = Math.round(cell * dpr);
  tile.height = Math.round(cell * dpr);
  const tctx = tile.getContext('2d')!;
  tctx.fillStyle = '#fff';
  tctx.fillRect(0, 0, Math.max(1, Math.round(dotSize * dpr)), Math.max(1, Math.round(dotSize * dpr)));

  const fw = Math.ceil(cols / FIELD_STEP) + 1;
  const fh = Math.ceil(rows / FIELD_STEP) + 1;

  return {
    cols,
    rows,
    cell,
    image: null,
    noise: buildNoise(),
    small,
    smallCtx,
    pixels: smallCtx.createImageData(cols, rows),
    mask: smallCtx.createPattern(tile, 'repeat'),
    fw,
    fh,
    vx: new Float32Array(fw * fh),
    vy: new Float32Array(fw * fh),
    energy: new Float32Array(fw * fh),
    tmp: new Float32Array(fw * fh),
  };
}

/** Sample the image into per-cell brightness, fitted across the width and centred at `centerY` (0..1). */
export function sampleImage(
  scene: DitherScene,
  img: HTMLImageElement,
  centerY: number,
  fit: { widthScale?: number; minHeight?: number } = {},
) {
  const { cols, rows } = scene;
  const { widthScale = 1.08, minHeight = 0.6 } = fit;
  const c = document.createElement('canvas');
  c.width = cols;
  c.height = rows;
  const ctx = c.getContext('2d');
  if (!ctx) return;
  const aspect = img.naturalWidth / img.naturalHeight;
  let w = cols * widthScale;
  let h = w / aspect;
  // Desktop keeps a minimum height so the hands fill the frame; phones skip it so both hands stay in view
  if (h < rows * minHeight) {
    h = rows * minHeight;
    w = h * aspect;
  }
  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, cols, rows);
  ctx.drawImage(img, (cols - w) / 2, rows * centerY - h / 2, w, h);
  try {
    const data = ctx.getImageData(0, 0, cols, rows).data;
    const out = new Float32Array(cols * rows);
    for (let i = 0; i < out.length; i++) {
      const lum = (data[i * 4] * 0.299 + data[i * 4 + 1] * 0.587 + data[i * 4 + 2] * 0.114) / 255;
      out[i] = Math.max(0, Math.min(1, (lum - 0.05) / 0.85));
    }
    scene.image = out;
  } catch {
    scene.image = null;
  }
}

type Pointer = { x: number; y: number; vx: number; vy: number; active: boolean };

export type HoverMode = 'trail' | 'ripple' | 'lens' | 'dissolve' | 'magnet';
export const TRAIL_LIFE = 0.5; // s — each touch point fades out quickly
const TRAIL_R = 15; // cells (~45px) — tight, local footprint
export type Ripple = { x: number; y: number; age: number }; // px, seconds
export const RIPPLE_LIFE = 2.4; // s
const RIPPLE_SPEED = 70; // cells per second
const RIPPLE_WIDTH = 5; // cells
const LENS_R = 48; // cells (~145px)
const MAGNET_R = 62; // cells (~185px)

/** Advance the cursor velocity field: inject motion, diffuse and decay. */
export function stepField(scene: DitherScene, pointer: Pointer, dt: number) {
  const { fw, fh, vx, vy, energy, tmp, cell } = scene;
  if (pointer.active) {
    const px = pointer.x / cell / FIELD_STEP;
    const py = pointer.y / cell / FIELD_STEP;
    const r = 4.5;
    const speed = Math.hypot(pointer.vx, pointer.vy);
    const x0 = Math.max(0, Math.floor(px - r));
    const x1 = Math.min(fw - 1, Math.ceil(px + r));
    const y0 = Math.max(0, Math.floor(py - r));
    const y1 = Math.min(fh - 1, Math.ceil(py + r));
    for (let y = y0; y <= y1; y++) {
      for (let x = x0; x <= x1; x++) {
        const d = Math.hypot(x - px, y - py) / r;
        if (d >= 1) continue;
        const f = (1 - d) * (1 - d);
        const i = y * fw + x;
        // velocity in cells per second, scaled down so the smear stays elegant
        vx[i] += (pointer.vx / cell) * f * 0.01;
        vy[i] += (pointer.vy / cell) * f * 0.01;
        energy[i] = Math.min(1, energy[i] + f * Math.min(1, speed / 700) * 0.5);
      }
    }
  }

  // Diffuse (3×3 box blend) + decay
  const decay = Math.exp(-1.6 * dt);
  const eDecay = Math.exp(-1.2 * dt);
  const diffuse = (arr: Float32Array, k: number) => {
    for (let y = 0; y < fh; y++) {
      for (let x = 0; x < fw; x++) {
        const i = y * fw + x;
        const l = x > 0 ? arr[i - 1] : arr[i];
        const r = x < fw - 1 ? arr[i + 1] : arr[i];
        const u = y > 0 ? arr[i - fw] : arr[i];
        const d = y < fh - 1 ? arr[i + fw] : arr[i];
        tmp[i] = (arr[i] * 0.6 + (l + r + u + d) * 0.1) * k;
      }
    }
    arr.set(tmp);
  };
  diffuse(vx, decay);
  diffuse(vy, decay);
  diffuse(energy, eDecay);
}

export function renderDither(
  ctx: CanvasRenderingContext2D,
  scene: DitherScene,
  o: {
    width: number;
    height: number;
    time: number;
    intro: number;
    color: [number, number, number];
    parallax: { x: number; y: number }; // in cells
    glow: { x: number; y: number; strength: number }; // eased cursor, px
    mode: HoverMode;
    ripples: Ripple[];
  },
) {
  const { cols, rows, cell, image, noise, pixels, small, smallCtx, mask, fw, vx, vy, energy } = scene;
  const { width, height, time, intro } = o;
  const buf = new Uint32Array(pixels.data.buffer);
  buf.fill(0);

  // Clouds drift slowly in two directions
  // Haze sits on a farther plane: it drifts the opposite way, by less
  const hx = -o.parallax.x * 0.4;
  const hy = -o.parallax.y * 0.4;
  const ax = time * 2.2 + hx;
  const ay = time * 0.8 + hy;
  const bx = -time * 1.4 + hx * 0.6;
  const by = time * 1.9 + hy * 0.6;

  const ccx = cols / 2;
  const ccy = rows * 0.5;
  const maxD = Math.hypot(ccx, ccy);
  const front = intro * maxD * 0.6;
  const introDone = front > maxD * 1.3;

  const [R, G, B] = o.color;
  const rgb = (B << 16) | (G << 8) | R;
  const EDGE = 0.24; // fraction of the width used for the left/right fade
  const glowOn = o.glow.strength > 0.01;
  const gcx = o.glow.x / cell;
  const gcy = o.glow.y / cell;
  const gR = 70; // radius in cells (~210px)
  const gInv = 1 / (gR * gR);
  const glowAmt = 0.09 * o.glow.strength;
  const mode = o.mode;
  const gs = o.glow.strength;
  // Ripple rings in cell space, with their current radius and fading amplitude
  const rings = mode === 'ripple'
    ? o.ripples.map((r) => {
        const life = r.age / RIPPLE_LIFE;
        const rad = r.age * RIPPLE_SPEED;
        const outer = rad + RIPPLE_WIDTH * 2;
        const inner = Math.max(0, rad - RIPPLE_WIDTH * 2);
        return { x: r.x / cell, y: r.y / cell, rad, amp: (1 - life) * (1 - life), o2: outer * outer, i2: inner * inner };
      })
    : [];

  // Trail: splat each recent touch point into small per-cell buffers (only its bounding box is visited)
  let tLift: Float32Array | null = null;
  let tDx: Float32Array | null = null;
  let tDy: Float32Array | null = null;
  if (mode === 'trail' && o.ripples.length) {
    const n = cols * rows;
    if (!scene.tLift || scene.tLift.length !== n) {
      scene.tLift = new Float32Array(n);
      scene.tDx = new Float32Array(n);
      scene.tDy = new Float32Array(n);
    }
    tLift = scene.tLift;
    tDx = scene.tDx!;
    tDy = scene.tDy!;
    tLift.fill(0);
    tDx.fill(0);
    tDy.fill(0);
    for (const p of o.ripples) {
      const life = 1 - p.age / TRAIL_LIFE;
      if (life <= 0) continue;
      const amp = life * life;
      const pcx = p.x / cell;
      const pcy = p.y / cell;
      const x0 = Math.max(0, Math.floor(pcx - TRAIL_R));
      const x1 = Math.min(cols - 1, Math.ceil(pcx + TRAIL_R));
      const y0 = Math.max(0, Math.floor(pcy - TRAIL_R));
      const y1 = Math.min(rows - 1, Math.ceil(pcy + TRAIL_R));
      for (let yy = y0; yy <= y1; yy++) {
        for (let xx = x0; xx <= x1; xx++) {
          const dx = xx - pcx;
          const dy = yy - pcy;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d >= TRAIL_R) continue;
          const f = (1 - d / TRAIL_R) * (1 - d / TRAIL_R) * amp;
          const i = yy * cols + xx;
          if (f > tLift[i]) tLift[i] = f;
          const inv = d > 0.5 ? 1 / d : 0;
          tDx[i] += dx * inv * f;
          tDy[i] += dy * inv * f;
        }
      }
    }
  }
  // Subtle living flicker: a small random threshold jitter that reshuffles ~10× per second
  const seed = ((time * 10) | 0) * 7919;


  for (let y = 0; y < rows; y++) {
    const fyRow = Math.min(scene.fh - 1, Math.floor(y / FIELD_STEP)) * fw;
    const vyN = y / rows;
    for (let x = 0; x < cols; x++) {
      const fi = fyRow + Math.min(fw - 1, Math.floor(x / FIELD_STEP));
      let sx = x;
      let sy = y;
      let lift = 0; // extra density from the hover effect
      let jitter = 0; // extra threshold scatter from the hover effect

      if (tLift) {
        // Direct touch: dots right under the cursor scatter a few px outward and flare, then settle fast
        const i = y * cols + x;
        const f = tLift[i];
        if (f > 0.002) {
          sx -= tDx![i] * 3.2;
          sy -= tDy![i] * 3.2;
          lift += f * 0.35;
          jitter = f * 0.35;
        }
      } else if (mode === 'ripple') {
        // Rings spread out from where the cursor travelled, bending the image like water
        for (let k = 0; k < rings.length; k++) {
          const g = rings[k];
          const dx = x - g.x;
          const dy = y - g.y;
          const d2 = dx * dx + dy * dy;
          if (d2 > g.o2 || d2 < g.i2) continue;
          const d = Math.sqrt(d2) || 1;
          const t = (d - g.rad) / RIPPLE_WIDTH;
          const wave = Math.exp(-t * t) * g.amp;
          sx -= (dx / d) * wave * 2.2 * -t;
          sy -= (dy / d) * wave * 2.2 * -t;
          lift += wave * 0.16;
        }
      } else if (glowOn && mode === 'lens') {
        // A soft magnifying glass: the image swells under the cursor, with a faint rim of light
        const dx = x - gcx;
        const dy = y - gcy;
        const d2 = dx * dx + dy * dy;
        if (d2 < LENS_R * LENS_R) {
          const q = Math.sqrt(d2) / LENS_R;
          const zoom = 1 - (1 - q * q) * 0.42 * gs;
          sx = gcx + dx * zoom;
          sy = gcy + dy * zoom;
          const rim = Math.exp(-((q - 0.93) * (q - 0.93)) / 0.0025);
          lift += rim * 0.22 * gs + (1 - q) * 0.05 * gs;
        }
      } else if (glowOn && mode === 'magnet') {
        // Gravity well: the image is drawn in toward the cursor and gathers density there
        const dx = x - gcx;
        const dy = y - gcy;
        const d2 = dx * dx + dy * dy;
        if (d2 < MAGNET_R * MAGNET_R && d2 > 0.5) {
          const d = Math.sqrt(d2);
          const f = 1 - d / MAGNET_R;
          const pull = f * f * 9 * gs;
          sx += (dx / d) * pull;
          sy += (dy / d) * pull;
          lift += f * f * f * 0.14 * gs;
        }
      } else if (mode === 'dissolve') {
        // Where the cursor passes, dots scatter like sand in the wind, then slowly re-form
        const e = energy[fi];
        if (e > 0.01) {
          const r1 = RAND[(y * cols + x) * 7 + seed & RAND_MASK] - 0.5;
          const r2 = RAND[(y * cols + x) * 11 + seed + 31 & RAND_MASK] - 0.5;
          sx += r1 * e * 14 - vx[fi] * 2;
          sy += r2 * e * 14 - vy[fi] * 2;
          jitter = e * 0.45;
        }
      }
      const ix = sx < 0 ? 0 : sx >= cols ? cols - 1 : sx | 0;
      const iy = sy < 0 ? 0 : sy >= rows ? rows - 1 : sy | 0;

      const n1 = noise[((((iy + ay) | 0) % NOISE + NOISE) % NOISE) * NOISE + ((((ix + ax) | 0) % NOISE + NOISE) % NOISE)];
      const n2 = noise[((((iy * 0.6 + by) | 0) % NOISE + NOISE) % NOISE) * NOISE + ((((ix * 0.6 + bx) | 0) % NOISE + NOISE) % NOISE)];
      const n = n1 * 0.55 + n2 * 0.45;

      // Cloud haze: density follows the noise, a little heavier toward the bottom
      let v = Math.max(0, (n - 0.38) * 1.15) * (0.55 + vyN * 0.55);
      let hand = false;
      let handTone = 0;
      if (image) {
        // Parallax: the hands sit on a nearer plane than the haze, so they shift a little more
        let px = (ix - o.parallax.x) | 0;
        let py = (iy - o.parallax.y) | 0;
        px = px < 0 ? 0 : px >= cols ? cols - 1 : px;
        py = py < 0 ? 0 : py >= rows ? rows - 1 : py;
        const im = image[py * cols + px];
        if (im > 0.02) {
          const hv = im * 1.03 + (n - 0.5) * 0.1;
          if (hv > v) {
            v = hv;
            hand = true;
            handTone = im;
          }
        }
      }
      v += lift * (hand ? 1 : 0.6);
      if (v > 0.04) v += (RAND[(y * cols + x) * 13 + seed & RAND_MASK] - 0.5) * (FLICKER + jitter);
      // A quiet, wide lift in density around the (eased) cursor
      if (glowOn && mode !== 'dissolve' && mode !== 'trail') {
        const gx = x - gcx;
        const gy = y - gcy;
        const g2 = (gx * gx + gy * gy) * gInv;
        if (g2 < 1) {
          const f = 1 - g2;
          v += f * f * glowAmt * 0.6 * (hand ? 1 : 0.5);
        }
      }

      if (!introDone) {
        const dist = Math.hypot(x - ccx, y - ccy);
        v *= Math.max(0, Math.min(1, (front - dist) / (maxD * 0.22)));
      }

      // Smooth fade toward the left and right edges of the screen
      const ex = x / cols;
      const e = ex < EDGE ? ex / EDGE : ex > 1 - EDGE ? (1 - ex) / EDGE : 1;
      const edge = e * e * (3 - 2 * e);
      v *= 0.55 + 0.45 * edge;

      if (v > BAYER_8[(x & 7) + ((y & 7) << 3)]) {
        let a: number;
        if (hand) {
          // Tonal depth: highlights brighter than shadowed areas, a slow cloud of light drifting
          // through the hands, and more presence toward the meeting fingertips in the centre
          const im = handTone;
          const cloud = 0.7 + (n - 0.5) * 0.9;
          const centre = 1 - Math.min(1, Math.abs(ex - 0.5) * 1.6) * 0.35;
          a = 255 * (0.32 + im * 0.78) * cloud * centre * edge;
        } else {
          a = 95 * (0.6 + n * 0.6) * edge;
        }
        if (a > 6) buf[y * cols + x] = ((Math.min(255, a) | 0) << 24) | rgb;
      }
    }
  }

  smallCtx.putImageData(pixels, 0, 0);
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
  ctx.imageSmoothingEnabled = false;
  const sx = ctx.canvas.width / width;
  ctx.drawImage(small, 0, 0, cols * cell * sx, rows * cell * sx);
  if (mask) {
    ctx.globalCompositeOperation = 'destination-in';
    ctx.fillStyle = mask;
    ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
  }
  ctx.restore();
  void height;
}
